import {
  Component, DestroyRef, EventEmitter, inject, Input, OnChanges, OnInit,
  Output, SimpleChanges, ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { CopyDirective } from '../../directives/copy.directive';
import { CommonService } from '../../services/common.service';
import { SocketService } from '../../services/socket.service';
import { WebStorageService } from '../../services/web-storage.service';
import { CartService } from '../../services/cart.service';
import { AccountService } from '../../services/account.service';
import { Router } from '@angular/router';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { AccessControlContractService } from '../../services/access-control-contract.service';
import { Erc20ContractService } from '../../services/erc20-contract.service';
import { AllowanceService } from 'src/app/shared/services/allowance.service';
import { ContractUtils } from '../../utils/contract-utils';
import { ExchangeContractService } from '../../services/exchange-contract.service';
import { environment } from 'src/environments/environment';
import { TransactionService } from '../../services/transaction.service';
import { NftContractService } from '../../services/nft-contract.service';
import { formatEther } from 'viem';
import { ExchangeService } from '../../services/exchange.service';
import { DomSanitizer } from '@angular/platform-browser';
import { ConfirmationTypeEnum } from '../../utils/confirmation-popup-enum';
import { NgIf, NgFor, NgStyle, DecimalPipe } from '@angular/common';
import { MailverficationComponent } from '../mailverfication/mailverfication.component';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';
import { ImageFallbackDirective } from '../../directives/image-fallback.directive';
import { PinataThumbnailPipe } from 'src/app/shared/pipes/pinata-thumbnail.pipe';

@Component({
    selector: 'app-my-cart',
    templateUrl: './my-cart.component.html',
    styleUrls: ['./my-cart.component.css'],
    imports: [PinataThumbnailPipe, NgIf, NgFor, NgStyle, ModalDirective, MailverficationComponent, ConfirmationModalComponent, DecimalPipe, ImageFallbackDirective]
})
export class MyCartComponent implements OnChanges, OnInit {

  @ViewChild('verificationModal') verificationModal?: ModalDirective;
  @ViewChild('confirmationModal') confirmationModal?: ModalDirective;
  @ViewChild('myCartModal', { static: false }) myCartModal?: ModalDirective;


  @Output() closeModal = new EventEmitter<void>();
  @Output() openModal = new EventEmitter<void>();

  @Input() cartData: any;
  progressData: any = null;
  totalItemValue: number = 0;
  currencySymbol: string = '';
  removeItemId: string | null = null;

  account: any;
  user: any;
  regulated: boolean = false;

  showRemoveConfirm = false;
  showBuyConfirm = false;
  isRemoving = false;

  platformFee: number = 0;
  totalFee: number = 0;

  feePercentage!: number;
  _fixedFeeAmount!: number;
  isBuyNowProcessing: boolean = false;
  confirmationData: any = {};
  hasInactiveItems: boolean = false;
  sellerPlatformFee: any;

  private readonly destroyRef = inject(DestroyRef);
  private readonly allowanceService = inject(AllowanceService);

  constructor(
    private webStorageService: WebStorageService,
    public commonService: CommonService,
    private copyDirective: CopyDirective,
    private fb: FormBuilder,
    private toastrService: ToastrService,
    private socketService: SocketService,
    private cartService: CartService,
    private accountService: AccountService,
    private router: Router,
    private accessControlContractService: AccessControlContractService,
    private erc20ContractService: Erc20ContractService,
    private contractUtils: ContractUtils,
    private exchangeContractService: ExchangeContractService,
    private transactionService: TransactionService,
    private nftContractService: NftContractService,
    private exchangeService: ExchangeService,
    private sanitizer: DomSanitizer,
  ) { }



  /**
   * Initializes the component by retrieving the account, user and regulated status from local storage.
   * If the account, user or regulated status is not found in local storage, it defaults to null or false respectively.
   */
  async ngOnInit(): Promise<void> {
    this.account = JSON.parse(this.webStorageService.getLocalStorage('account') || 'null');
    this.user = JSON.parse(this.webStorageService.getLocalStorage('user') || 'null');
    this.regulated = JSON.parse(this.webStorageService.getLocalStorage('regulated') || 'false');

    this.getSellOrderStatusUpdate();

    let conractFunction = ['buyerPlatformFeePercentage', 'buyerPlatformFeeFixedAmount'];
    let contract = 1;

    // WAIT for the promise
    if (this.account && this.user) {
      this.sellerPlatformFee = await this.getSellerFeeDetails(conractFunction, contract);
    }
  }


  /**
   * Called when the component's input properties change.
   * Checks if the cart data has changed and recalculates the total value of the items in the cart.
   * If the cart is empty, emits the close modal event.
   * @param changes the changes to the component's input properties
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes['cartData'] && this.cartData) {
      this.hasInactiveItems = this.cartData.some((item: any) => !item?.is_active);
      this.calculateTotal();
    }
  }

  /**
     * Listens to the socket service for updates to the sell order status.
     * When a sell order status update is received, it will emit a cart update event.
     */
  getSellOrderStatusUpdate() {
    this.socketService.getSellOrderUpdate()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.cartService.emitCartUpdate();
      });
  }



  /**
   * Calculates the total value of the items in the cart and sets the currency symbol
   */
  calculateTotal() {
    if (!this.cartData?.length) {
      this.totalItemValue = 0;
      this.currencySymbol = '';
      return;
    }

    this.currencySymbol = this.cartData[0]?.sell_order_id?.currency?.display_symbol || '';
    this.totalItemValue = this.cartData.reduce(
      (sum: number, item: any) => sum + (item.sell_order_id?.nft?.sale_price || 0),
      0
    );
  }



  /**
   * Sets the component state to show a confirmation modal for removing an item from the cart.
   * The modal will display the item id and a cancel/remove button.
   * If the user clicks the remove button, the item will be removed from the cart.
   * If the user clicks the cancel button, the modal will be closed and no changes will be made to the cart.
   * @param id - The id of the item to be removed from the cart.
   */
  removeItem(id: string) {
    this.removeItemId = id;
    this.showRemoveConfirm = true;
  }

  /**
   * Cancels the removal of the item from the cart.
   * Resets the confirm remove modal state and removes the item id from the component state.
   */
  cancelRemove() {
    this.showRemoveConfirm = false;
    this.removeItemId = null;
    this.isRemoving = false;
  }

  /**
   * Confirms the removal of the item from the cart.
   * If the item is successfully removed, shows a success toast and emits a cart update event.
   * If the removal operation fails, shows an error toast with the error message.
   * If an unexpected error occurs, shows an error toast with a generic error message.
   */
  confirmRemove() {
    if (!this.removeItemId || this.isRemoving) return;

    this.isRemoving = true;
  
    // 1 Remove locally first (instant UI update)
    if (this.removeItemId === 'all') {
      // Remove locally
      this.cartData = [];
    }else{
      this.cartData = this.cartData.filter(
        (item: any) => item._id !== this.removeItemId
      );
    }
  
    // 2️ If cart is empty after local removal, close modal
    if (!this.cartData.length) {
      this.closeModal.emit();
    }
  
    // 3️ Proceed with API call
    this.cartService.removeCart(this.removeItemId).subscribe({
      next: () => {
        this.toastrService.success("Asset removed from cart.");
        this.cartService.emitCartUpdate();
        this.cartService.purchaseCompleted$.next({
          success: true
        });
      },
      error: (err) => {
        this.toastrService.error(err?.error?.message || "Failed to remove asset.");
        this.isRemoving = false;
      },
      complete: () => {
        this.showRemoveConfirm = false;
        this.removeItemId = null;
        this.isRemoving = false;
      }
    });
  }
  



  /**
   * Buys the NFT order in the cart.
   * @description
   * Checks if user is registered and if KYC is verified.
   * If KYC is not verified, navigates to KYC page.
   * If user is not registered, navigates to sign-up page.
   * Calculates the total fee (including platform fee) and shows a confirmation dialog.
   * If user confirms, buys the NFT order.
   * @returns {void}
   */
  async buyNftOrder() {
    try {
      this.isBuyNowProcessing = true;
      const kycStatus: any = await this.accountService.getKycStatus();

      if (kycStatus?.data?.admin_kyc_enable && kycStatus?.data?.kyc_enable &&
        kycStatus?.data?.kyc_verified !== 1) {
        this.toastrService.warning("Identity confirmation required.");
        this.setRoute();
        return this.router.navigate(['kyc']);
      }

      if (!this.regulated) {
        if (!this.user) {
          this.setRoute();
          this.toastrService.warning("Register to continue.");
          return this.router.navigate(['sign-up']);
        } else if (!this.user.email_verified || !this.user?.is_valid) {
          this.verificationModal?.show();
          return
        }
      }
      const blocked = await this.accessControlContractService.isBlocked(this.account);

      if (blocked) {
        return this.toastrService.error("User blocked. Contact admin.");
      }
      if (!(await this.accessControlContractService.ensureRegistered(this.account))) return;

      this.platformFee = this.calculatePlatformFee();
      this.totalFee = this.totalItemValue + this.platformFee;
      this.showBuyConfirm = true;
      this.prepareBuyConfirmationData();
      this.confirmationModal?.show();

    } catch (err) {
      console.error(err);
    } finally {
      // Reset on every exit path — including the blocked / not-registered early returns —
      // so the "Buy now" button never stays stuck in its processing state.
      this.isBuyNowProcessing = false;
    }
  }

  /**
   * Prepares confirmation data for buying an NFT order.
   * Gets the active items in the cart, calculates the total price and
   * constructs a confirmation message.
   * If there is only one item in the cart, it constructs a single NFT
   * message. If there are multiple items, it constructs a bulk purchase message.
   * The confirmation data is used to show a confirmation dialog to the user.
   */
  private prepareBuyConfirmationData() {
    const activeItems = this.cartData.filter((i: any) => i?.is_active);
    const itemCount = activeItems.length;

    const totalPrice = activeItems.reduce(
      (sum: any, item: any) => sum + Number(item.sell_order_id.price),
      0
    );

    const currencySymbol =
      activeItems[0]?.sell_order_id?.currency?.display_symbol || "";

    // SINGLE NFT MESSAGE
    let headingContent = `
      <p class="fs-6 m-0">
        Do you want to proceed with purchasing
        <b>${activeItems[0]?.sell_order_id?.nft?.name}</b>
        for <b>${Math.ceil(totalPrice)} ${currencySymbol}</b>?
      </p>
    `;

    // BULK PURCHASE MESSAGE
    if (itemCount > 1) {
      headingContent = `
        <p class="fs-6 m-0">
          Do you want to proceed with purchasing
          <b>${itemCount} assets</b> for a total of
          <b>${Math.ceil(this.totalItemValue)} ${currencySymbol}</b>?
        </p>
      `;
    }

    this.confirmationData = {
      image: activeItems.map((item: any) => ({
        displayImage: item.sell_order_id.nft.primary_media,
        name: item.sell_order_id.nft.name,
        price: item.sell_order_id.nft.sale_price,
        currencySymbol: item.sell_order_id.currency.display_symbol
      })),
      confirmation_type: ConfirmationTypeEnum.BUY,
      heading_content: headingContent,
      footer_content: this.getPlatformFeeText()
    };
  }

  /**
   * Gets the platform fee details from the contract.
   * @param {string[] | any} functionName - The function name to be used for calculating the platform fee.
   * @param {number} type - The type of the transaction.
   * @returns {{feePercentage: number, _fixedFeeAmount: number}} - The calculated platform fee details.
   */
  async getSellerFeeDetails(functionName: string[] | any, type: number) {
    let contractAddress: any;
    this.nftContractService.handleContractAddress(type, (contractName: string) => {
      contractAddress = (environment as any)[this.account?.chainId][contractName]
    })
    let platformFeePercentage = await this.nftContractService.getplatformfee(contractAddress, functionName, type)
    // platformFeePercentage[0] = For Ever We pass the first position for platform Percentage 
    // platformFeePercentage[1] = For Ever We pass the first position for platform Fixed Amount 
    const feePercentage = platformFeePercentage[0]?.result ? Number(platformFeePercentage[0]?.result) / 100 : 0;
    //console.log(feePercentage, "Platform Fee Percentage")//For Debugging/Testing Purpose
    const _fixedFeeAmount = platformFeePercentage[1]?.result ? Number(platformFeePercentage[1]?.result) : 0;
    return { feePercentage, _fixedFeeAmount }
  }




  /**
   * Calculates the total platform fee based on the seller's platform fee percentage and fixed amount.
   * @returns {number} The total platform fee.
   */
  calculatePlatformFee() {
    if (!this.sellerPlatformFee) return 0;

    const percentage = this.sellerPlatformFee.feePercentage / 100;
    const fixedFee = this.sellerPlatformFee._fixedFeeAmount;

    return (this.totalItemValue * percentage) + fixedFee;
  }


  /**
   * Saves the current route in the web storage.
   * This allows the user to be redirected to the previous route after completing certain actions.
   */
  setRoute() {
    this.webStorageService.setItem('previousRoute', this.router.url);
  }


  /**
   * Cancels the bulk purchase confirmation modal and executes the bulk purchase
   * from the cart.
   */

  confirmBulkPurchase() {
    this.showBuyConfirm = true;
    this.bulkBuyAllFromCart();
  }

  /**
   * Hides the confirmation modal and resets the showBuyConfirm flag to false.
   * Also closes the my cart modal.
   */
  onConfirmationModalClosure() {
    this.showBuyConfirm = false;
    this.confirmationModal?.hide();
    this.closemyCartModal();
    this.cartService.emitCartUpdate();
  }


  /**
   * Returns an object containing the total item value, platform fee, currency symbol, and total fee.
   * This object is used as the form data for the bulk purchase confirmation modal.
   * @returns {{totalItemValue: number, platformFee: number, currency_data: {symbol: string}, price: number}}
   */
  getFormData() {
    return {
      totalItemValue: this.totalItemValue,
      platformFee: this.platformFee,
      currency_data: {
        symbol: this.currencySymbol
      },
      price: this.totalFee,

    };
  }



  /**
   * Cancels the bulk purchase confirmation modal.
   */
  cancelConfirmation() {
    this.showBuyConfirm = false;
  }



  /**
   * Closes the my cart modal.
   * Emits the close modal event.
   */
  closemyCartModal() {
    this.showBuyConfirm = false;
    this.closeModal.emit();
  }




  /**
   * Submits a bulk purchase of all items in the cart.
   * @remarks
   * - Checks if user is blocked
   * - Prepares orders
   * - Checks balance
   * - Ensures approval
   * - Executes bulk order
   * - Saves purchases
   * - Updates cart
   */
  async bulkBuyAllFromCart() {
    this.setupBulkProgressModal();

    try {
      // STEP 0 → Approve
      this.markStepRunning(0);

      // Gate: blocked OR not registered on-chain. isPurchaseBlocked() shows the correct,
      // distinct toast for each case itself — so fail step 0 without re-toasting here.
      // (Previously this threw "User is blocked.", which mislabeled unregistered users and
      // stacked a duplicate toast via the catch block.)
      if (await this.isPurchaseBlocked()) {
        this.progressData.steps[0].status = 3;
        this.progressData.failed = true;
        return;
      }

      if (this.isCartFrozen()) {
        this.toastrService.error('One or more items in your cart are frozen by admin and cannot be purchased. Remove them and try again.');
        this.progressData.steps[0].status = 3;
        this.progressData.failed = true;
        return;
      }

      const { orders, totalPrice, currencyAddress, currencyId } = await this.prepareOrders();

      const { totalPriceDecimal, balanceRaw, decimals } = await this.checkBalance(totalPrice, currencyAddress);
      let balance = await this.erc20ContractService.getBalance(this.account?.networkId, currencyAddress, this.account?.walletAddress);
      let formattedBalance = this.contractUtils.decimalDivider(decimals, balance);

      if (Number(formattedBalance) < Number(this.totalFee)) {
        this.markFailed(0, this.regulated ? `Insufficient balance to bulk purchase. You have ${Number(formattedBalance).toFixed(2)} ${this.currencySymbol} in your wallet, but you need ${this.totalFee} ${this.currencySymbol}. To fund your wallet, please contact your account manager.` : `Insufficient balance to bulk purchase. You have ${Number(formattedBalance).toFixed(2)} ${this.currencySymbol} in your wallet, but you need ${this.totalFee} ${this.currencySymbol}.`);
        return;
      }

      await this.ensureApproval(currencyAddress, currencyId, totalPriceDecimal);

      this.markStepCompleted(0);

      // STEP 1 → Complete bulk order
      this.markStepRunning(1);

      const bulkTx = await this.executeBulkOrder(orders);

      const result = await this.savePurchases(bulkTx);
      if(result){
        this.cartService.purchaseCompleted$.next({
          success: true
        });
        this.markStepCompleted(1);
      }

    } catch (err: any) {

      if (err?.message?.includes("User rejected")) {
        this.toastrService.error("Transaction rejected by user.");
      } else {
        this.toastrService.error(err?.message || "Bulk purchase failed.");
      }
      const stepIndex = this.progressData.currentStep;
      this.markFailed(stepIndex, err?.message || "Bulk purchase failed.");

    }
  }

  /**
   * Marks a step as running.
   * @param stepIndex The index of the step to be marked as running.
   * @remarks
   * - Sets the status of the step at the given index to 1.
   * - Sets the status of all previous steps to 2.
   * - Sets the current step index to the given index.
   */
  private markStepRunning(stepIndex: number) {
    this.progressData.steps.forEach((s: any, i: number) => {
      if (i === stepIndex) s.status = 1;
      else if (i < stepIndex) s.status = 2;
    });
    this.progressData.currentStep = stepIndex;
  }

  /**
   * Marks a step as completed.
   * @param stepIndex The index of the step to be marked as completed.
   * @remarks
   * - Sets the status of the step at the given index to 2.
   */
  private markStepCompleted(stepIndex: number) {
    this.progressData.steps[stepIndex].status = 2;
  }
  

  /**
   * Marks a step as failed.
   * @param stepIndex The index of the step to be marked as failed.
   * @param message The error message to display to the user.
   * @remarks
   * - Sets the status of the step at the given index to 3.
   * - Sets the failed flag to true.
   * - Displays an error toast with the given message.
   */
  private markFailed(stepIndex: number, message: string) {
    this.progressData.steps[stepIndex].status = 3;
    this.progressData.failed = true;
    this.toastrService.error(message);
  }

/**
 * Sets up the progress modal for bulk purchases.
   * @remarks
   * - Retrieves the total purchase price and currency symbol.
   * - Calculates the platform fee with dynamic values.
   * - Updates the modal data with the calculated platform fee and other relevant information.
   */
  // 1) In setupBulkProgressModal() - set plain text successTitle (already had successTitleHtml before)
// 1) setupBulkProgressModal: set sanitized HTML to successTitle
private setupBulkProgressModal() {
  const totalPrice = this.calculateTotalPurchasePrice();
  const currencySymbol = this.currencySymbol;
  const itemCount = this.cartData.filter((x: any) => x?.is_active).length;

  const percentage = this.sellerPlatformFee?.feePercentage || 0;
  const fixedAmount = this.sellerPlatformFee?._fixedFeeAmount || 0;

  const percentageFee = (totalPrice * (percentage / 100));
  const totalPlatformFee = percentageFee + fixedAmount;

  this.progressData = {
    title: `Purchas${this.regulated ? 'ing' : 'e'} ${itemCount} asset${itemCount > 1 ? 's' : ''} for ${totalPrice} ${currencySymbol}`,
    currentStep: 0,
    steps: [
      { title: "Approve currency.", status: 1 },
      { title: "Complete bulk order.", status: 0 }
    ],
    failed: false,
    platformFeeDetails: [
      {
        title: "Platform fee",
        value: `${this.commonService.formatDecimal(percentage)}% of purchase price + ${this.commonService.formatDecimal(fixedAmount)} ${currencySymbol}, due upon confirmation.`,
        totalDeductAmount: `${totalPlatformFee.toFixed(2)} ${currencySymbol}`,
        isBold: true
      }
    ],
    // set the sanitized HTML on successTitle (or keep successTitleHtml, but this keeps template simple)
    successTitle: `Bulk purchase completed and assets moved to your <a href="/my-wallet" class="text-decoration-underline cursor-pointer">wallet</a>.`
  };
}




  /**
   * Close the cart modal and redirect to /all-assets showing only purchasable
   * items (For sale + Sale by Broker + Sale by Owner, excluding any item
   * currently in collateral / on a loan). Other user filters (category,
   * location, collection, price, appraisal) are preserved.
   *
   * The dashboard reads `isAddCart` to keep webStorage filters from being
   * clobbered by URL params during ngOnInit (see dashboard.component.ts).
   */
  addMoreItems() {
    this.closemyCartModal();

    // Strip any stale sale/collateral filters so we can force the exact state
    // the user expects when clicking "Add more" from the cart. Other filters
    // (category, location, collection, price, etc.) are preserved.
    const purchasableFilterTypes = new Set(['sale', 'brokerSale', 'secondarySale', 'collateral']);
    const preserved = JSON.parse(this.webStorageService.getItem('filters') || '[]')
      .filter((f: any) => !purchasableFilterTypes.has(f?.type));

    preserved.push({ type: 'sale', value: 'yes', name: 'For sale.' });
    preserved.push({ type: 'brokerSale', value: 'yes', name: 'Sale by Broker' });
    preserved.push({ type: 'secondarySale', value: 'yes', name: 'Sale by Owner' });
    preserved.push({ type: 'collateral', value: 'no', name: 'Not in collateral' });

    this.webStorageService.setItem('filters', JSON.stringify(preserved));
    this.webStorageService.setItem('showDefaultNfts', 'false');
    localStorage.setItem('isAddCart', 'true');

    this.router.navigate(['/all-assets']);
  }





  /**
   * Gate for bulk purchase. Returns true (purchase not allowed) when the user is either
   * blocked OR not registered on-chain, and false when they may proceed.
   *
   * Each failing case surfaces its OWN distinct toast — "User blocked." when blocked, the
   * not-registered message when unregistered, and a verify-failed message on a read error —
   * so the caller must not add another toast on top.
   */
  /**
   * Whether any active cart item's NFT is frozen. Bundle purchase creation is Restricted on the
   * finalized freeze matrix (same bucket as buy/bid/sale creation) - mirrors isPurchaseBlocked()'s
   * shape/placement so the two gates read the same way at the call site.
   */
  private isCartFrozen(): boolean {
    return this.cartData?.some((item: any) => item?.is_active && item?.sell_order_id?.nft?.is_frozen) ?? false;
  }

  private async isPurchaseBlocked() {
    try {
      const blocked = await this.accessControlContractService.isBlocked(this.account);
      if (blocked) { this.toastrService.error("User blocked."); return true; }
      // Also require on-chain platform registration (shows its own toast if not registered).
      if (!(await this.accessControlContractService.ensureRegistered(this.account))) return true;
      return false;
    } catch (err) {
      console.error(err);
      this.toastrService.error("Unable to verify account status.");
      return true;
    }
  }

  /**
   * Prepares the orders from the cart data to be used in the bulk purchase.
   *
   * @returns {Promise<{orders: any[], totalPrice: number, currencyAddress: string}>}
   * Resolves with an object containing the prepared orders, total price and currency address.
   * Rejects with an error if there is an issue preparing the orders.
   */
  private async prepareOrders() {
    try {
      const orders = [];
      let totalPrice = 0;
      let currencyAddress = "";
      let currencyId = "";

      for (const item of this.cartData) {
        if (!item?.is_active) continue;
        const sell = item.sell_order_id;
        const nft = sell.nft;

        const price = Number(sell.price);
        const nonce = sell.nonce;

        const finalPrice = await this.calculateTotalAmount(
          price,
          ['buyerPlatformFeePercentage', 'buyerPlatformFeeFixedAmount'],
          1
        );

        totalPrice += finalPrice;

        const order = await this.exchangeContractService.organizeSellOrder(
          this.account?.networkId,
          sell.seller.wallet_address,
          nft.collections.collection_address,
          nft.token_id.toString(),
          sell.currency.address,
          price.toString(),
          nonce.toString()
        );

        orders.push(order);
        currencyAddress = sell.currency.address;
        currencyId = sell.currency._id;
      }

      return { orders, totalPrice, currencyAddress, currencyId };

    } catch (err) {
      console.error("Error preparing orders:", err);
      throw new Error("Failed to prepare orders.");
    }
  }

  /**
   * Checks the user's balance against the total price.
   * @param totalPrice the total price of the items in the cart.
   * @param currency the address of the ERC20 token used for the transaction.
   * @returns an object containing the total price in decimal form and the user's current balance.
   * @throws {Error} if the balance check fails.
   */
  private async checkBalance(totalPrice: number, currency: string) {
    try {
      const decimals: any = await this.erc20ContractService.getDecimal(this.account?.networkId, currency);
      const balanceRaw = await this.erc20ContractService.getBalance(
        this.account?.networkId,
        currency,
        this.account?.walletAddress
      );

      const totalPriceDecimal = this.contractUtils.decimalMultipler(decimals, totalPrice);

      return { totalPriceDecimal, balanceRaw, decimals };

    } catch (err) {
      console.error(err);
      throw new Error("Balance check failed.");
    }
  }

  /**
   * Sets the exchange allowance to exactly what this purchase and the user's open bids need.
   *
   * The exchange pulls the purchase from the same allowance the user's open bids rely on, so the
   * requirement is the bids plus this cart. An existing allowance is counted rather than stacked
   * on, and a surplus is trimmed back. No transaction is sent when it already matches.
   * @param currency - the address of the currency to be approved
   * @param currencyId - the currency's id, which the open-bid total is kept against
   * @param requiredAmount - what this purchase pulls, in the currency's smallest unit
   * @throws {Error} - if the approval fails
   */
  private async ensureApproval(currency: string, currencyId: string, requiredAmount: number | string) {
    try {
      const allowance = await this.allowanceService.computeCounterAllowance({
        account: this.account,
        currencyId,
        currencyAddress: currency,
        counterAmountWithFeesHuman: Number(this.totalFee),
        counterAmountWithFeesWei: BigInt(requiredAmount),
        spender: 'exchange'
      });

      if (allowance.action === 'none') return;

      const txData: any = await this.transactionService.createTransaction({
        from: this.account?.walletAddress,
        to: currency,
        from_id: this.user._id,
        transaction_name: 'Approve currency (Bulk)',
        nft_id: this.cartData.find((i: any) => i?.is_active)?.sell_order_id?.nft?._id,
        status: 0, amount: 0, currency_symbol: "-"
      });

      const approveArgs = {
        functionName: allowance.action === 'increase' ? 'increaseAllowance' : 'decreaseAllowance',
        args: [allowance.spenderAddress, allowance.deltaWei],
        abiType: 'erc20'
      };

      let approveTx: any = await this.commonService.sendTransaction(
        this.account, currency, approveArgs, txData.data._id
      );

      approveTx = this.regulated && approveTx?.data ? JSON.parse(approveTx.data) : approveTx;

      await this.transactionService.createTransaction(
        { status: 1, transaction_hash: approveTx?.transactionHash },
        txData.data._id
      );

      // this.toastrService.success("Currency approved successfully.");

    } catch (err: any) {
      if (err?.message?.includes("User rejected")) {
        this.toastrService.error("Approval rejected by user.");
      } else {
        this.toastrService.error("Approval failed.");
      }

      throw err;
    }
  }


  /**
   * Execute a bulk order.
   * @param orders Array of orders to be processed.
   * @returns The transaction object containing the transaction hash.
   * @throws Error if the bulk order fails.
   */
  private async executeBulkOrder(orders: any[]) {
    try {
      const spender = (environment as any)[this.account?.chainId]['EXCHANGE_CONTRACT'];

      const txData: any = await this.transactionService.createTransaction({
        from: this.account?.walletAddress,
        to: spender,
        from_id: this.user._id,
        transaction_name: 'Complete Purchase (Bulk)',
        nft_id: this.cartData.find((i: any) => i?.is_active)?.sell_order_id?.nft?._id,
        status: 0, amount: 0, currency_symbol: "-"
      });

      const args = {
        functionName: 'completeOrdersBatch',
        args: [orders],
        abiType: 'exchange'
      };

      let bulkTx: any = await this.commonService.sendTransaction(
        this.account, spender, args, txData.data._id, true
      );

      bulkTx = this.regulated && bulkTx?.data ? JSON.parse(bulkTx.data) : bulkTx;

      await this.transactionService.createTransaction(
        { status: 1, transaction_hash: bulkTx?.transactionHash },
        txData.data._id
      );

      return bulkTx;

    } catch (err) {
      console.error("Bulk order failed:", err);
      throw new Error("Bulk order failed.");
    }
  }



  /**
   * Saves the purchases in the database after a successful bulk purchase
   * @param bulkTx The transaction data from the bulk purchase
   * @returns A promise that resolves to the saved purchase data
   * @throws An error if the purchase save fails
   */
  private async savePurchases(bulkTx: any) {
    try {
      const sellOrders = this.cartData
        .filter((item: any) => item?.is_active)   // Include only active items
        .map((item: any) => ({
          id: item.sell_order_id._id,
          price: Number(item.sell_order_id.price),
          token_id: item.sell_order_id.nft.token_id
        }));


      const payload = {
        sell_orders: sellOrders,
        buyer: this.user._id,
        status: 1,
        price: this.calculateTotalPurchasePrice(),
        transaction_hash: bulkTx?.transactionHash,
        gas_price: bulkTx?.effectiveGasPrice
          ? formatEther(bulkTx.effectiveGasPrice.toString())
          : "",
        block_number: `${bulkTx?.blockNumber || ""}`,
        transaction_initiator: bulkTx?.from || this.account?.walletAddress
      };

      return await this.exchangeService.updateCompletedStatus(payload).toPromise();

    } catch (err) {
      console.error("Purchase save failed:", err);
      throw new Error("Failed to save purchase.");
    }
  }

  /**
   * Calculates the total purchase price from the cart data.
   * @returns {number} The total purchase price.
   */
  private calculateTotalPurchasePrice() {
    return this.cartData
      .filter((item: any) => item?.is_active) // include only active items
      .reduce(
        (sum: number, item: any) => sum + Number(item.sell_order_id.price),
        0
      );
  }


  /**
   * Calculates the total amount to be paid for a transaction based on the given
   * transaction amount, function name and type.
   * @param {number} transactionAmount - The amount of the transaction.
   * @param {any} fn - The function name to be used for calculating the platform fee.
   * @param {number} type - The type of the transaction.
   * @returns {number} The total amount to be paid for the transaction.
   */
  async calculateTotalAmount(transactionAmount: number, fn: any, type: number) {
    try {
      let contractAddress: any;
      this.nftContractService.handleContractAddress(type, (contractName: string) => {
        contractAddress = (environment as any)[this.account?.chainId][contractName]
      });

      const platformFeeData = await this.nftContractService.getplatformfee(
        contractAddress, fn, type
      );

      this.feePercentage = platformFeeData[0]?.result
        ? Number(platformFeeData[0].result) / 100
        : 0;

      this._fixedFeeAmount = platformFeeData[1]?.result
        ? Number(platformFeeData[1].result)
        : 0;

      const platformFee = (transactionAmount * this.feePercentage) / 100;
      return transactionAmount + platformFee + this._fixedFeeAmount;

    } catch (err) {
      console.error("Failed to calculate total amount:", err);
      return transactionAmount;
    }
  }

  /**
   * Gets the platform fee text based on the given values.
   * @returns {string} The platform fee text.
   */
  getPlatformFeeText() {
    const symbol = this.currencySymbol || '$';
    const percent = this.sellerPlatformFee?.feePercentage ?? 0;
    const fixed = this.sellerPlatformFee?._fixedFeeAmount ?? 0;

    return `Platform fee not included: ${this.commonService.formatDecimal(percent)}% + ${this.commonService.formatDecimal(fixed)} ${symbol}`;
  }

}
